import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import NotificationService, { officePushApi } from '../Services/NotificationService';
import { syncSubscription } from '../Services/PushNotifications';

const POLLING_INTERVAL = 30000; // 30 seconds

/**
 * Hook for managing dashboard notifications
 * - Polls for unread count every 30 seconds
 * - Triggers toast when new notifications arrive
 * - Provides methods to mark as read and fetch more
 *
 * @param {boolean} isAuthed - Whether the user is authenticated
 * @returns {Object} Notification state and methods
 */
const useNotifications = (isAuthed) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastKey, setLastKey] = useState(null);
  const [newNotification, setNewNotification] = useState(null);
  const [error, setError] = useState(null);

  // Use refs to track values without causing re-renders in dependencies
  const prevCountRef = useRef(null); // null = not initialized yet
  const pollingRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastKeyRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    lastKeyRef.current = lastKey;
  }, [lastKey]);

  /**
   * Fetch unread count (used for polling)
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthed) return;

    try {
      const result = await NotificationService.getUnreadCount();
      const newCount = result.count;

      // Detect if new notifications arrived (only after initial load)
      // prevCountRef.current is null on first poll, so we skip toast on initial load
      if (prevCountRef.current !== null && newCount > prevCountRef.current) {
        // Fetch the latest notification to show in toast
        const { notifications: latest } = await NotificationService.getNotifications({
          limit: 1,
          unreadOnly: true,
        });

        if (latest && latest.length > 0) {
          setNewNotification(latest[0]);
          // Update notifications list with new one at the top
          setNotifications((prev) => {
            // Avoid duplicates
            const exists = prev.some((n) => n.Id === latest[0].Id);
            if (exists) return prev;
            return [latest[0], ...prev];
          });
        }
      }

      // Only update state if count actually changed to prevent unnecessary re-renders
      if (prevCountRef.current !== newCount) {
        prevCountRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (err) {
      // Silently fail for polling - don't spam console
    }
  }, [isAuthed]);

  /**
   * Fetch notifications with pagination
   */
  const fetchNotifications = useCallback(async (reset = false) => {
    if (!isAuthed || isLoadingRef.current) return;

    setIsLoading(true);
    isLoadingRef.current = true;
    setError(null);

    try {
      const result = await NotificationService.getNotifications({
        limit: 20,
        lastKey: reset ? null : lastKeyRef.current,
      });

      if (reset) {
        setNotifications(result.notifications || []);
      } else {
        setNotifications((prev) => [...prev, ...(result.notifications || [])]);
      }

      const newLastKey = result.lastKey || null;
      setLastKey(newLastKey);
      lastKeyRef.current = newLastKey;
      setHasMore(!!result.lastKey);

      // Update unread count
      const countResult = await NotificationService.getUnreadCount();
      setUnreadCount(countResult.count);
      prevCountRef.current = countResult.count;
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [isAuthed]);

  /**
   * Fetch more notifications (pagination)
   */
  const fetchMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchNotifications(false);
    }
  }, [fetchNotifications, hasMore, isLoading]);

  /**
   * Refresh notifications (reset pagination)
   */
  const refresh = useCallback(() => {
    setLastKey(null);
    lastKeyRef.current = null;
    setHasMore(true);
    fetchNotifications(true);
  }, [fetchNotifications]);

  /**
   * Mark specific notifications as read
   */
  const markAsRead = useCallback(async (notificationIds) => {
    if (!isAuthed || !notificationIds?.length) return;

    try {
      await NotificationService.markAsRead(notificationIds);

      // Update local state
      setNotifications((prev) => prev.map((n) => (notificationIds.includes(n.Id)
        ? { ...n, IsRead: true, ReadAt: new Date().toISOString() }
        : n)));

      // Update count
      setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));
      prevCountRef.current = Math.max(0, prevCountRef.current - notificationIds.length);
    } catch (err) {
      // Handle error silently
    }
  }, [isAuthed]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!isAuthed) return;

    try {
      await NotificationService.markAllAsRead();

      // Update local state
      setNotifications((prev) => prev.map((n) => ({
        ...n,
        IsRead: true,
        ReadAt: new Date().toISOString(),
      })));

      setUnreadCount(0);
      prevCountRef.current = 0;
    } catch (err) {
      // Handle error silently
    }
  }, [isAuthed]);

  /**
   * Clear the new notification (after showing toast)
   */
  const clearNewNotification = useCallback(() => {
    setNewNotification(null);
  }, []);

  // Initial fetch and start polling
  useEffect(() => {
    if (isAuthed && !isInitializedRef.current) {
      isInitializedRef.current = true;

      // Initial fetch
      fetchNotifications(true);

      // Start polling
      pollingRef.current = setInterval(fetchUnreadCount, POLLING_INTERVAL);

      // Keep the server's copy of this browser's push subscription honest.
      // A browser can rotate or drop one without telling anyone (cleared
      // site data, a reinstalled PWA, an expired endpoint), and the result
      // would be a phone that quietly stopped receiving what the bell shows.
      // Silent, best-effort, and a no-op for anyone who never turned phone
      // notifications on. It also re-registers the push service worker,
      // which is how a new version of it is picked up.
      syncSubscription(officePushApi);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isAuthed]);

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthed) {
      isInitializedRef.current = false;
      setNotifications([]);
      setUnreadCount(0);
      setLastKey(null);
      lastKeyRef.current = null;
      prevCountRef.current = null;
    }
  }, [isAuthed]);

  return {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    error,
    newNotification,
    fetchMore,
    refresh,
    markAsRead,
    markAllAsRead,
    clearNewNotification,
  };
};

export default useNotifications;
